import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import type { TableNotificationType } from '@/types';

const NO_RECORDS_TITLE = 'No records found';
const NO_RECORDS_MESSAGE = 'There are no records that match your current filters.';
const NO_RECORDS_HINT = 'Change or remove filters to see available records.';

interface Props {
  type: TableNotificationType;
  message?: string;
  timeoutMs?: number;
  onEditFilterEvent?: () => void;
  onTryAgainEvent?: () => void;
}

const GLOBAL_TIMEOUT_MS = 30000;
const TIMER_TICK_MS = 250;

const CONFIG: Record<TableNotificationType, { severity: 'info' | 'warning' | 'error' | 'success'; title: string; defaultMessage: string }> = {
  Loading: { severity: 'info', title: 'Loading', defaultMessage: 'Fetching data, please wait...' },
  Filter:  { severity: 'info', title: 'No Results', defaultMessage: 'No records match the current filters.' },
  Error:   { severity: 'error', title: 'Error', defaultMessage: 'An error occurred. Please try again.' },
  Empty:   { severity: 'warning', title: 'No Data', defaultMessage: 'No data available.' },
  Timeout: { severity: 'warning', title: 'Timeout', defaultMessage: 'The request timed out. Please try again.' },
};

export const TableNotification: React.FC<Props> = ({
  type,
  message,
  timeoutMs = GLOBAL_TIMEOUT_MS,
  onEditFilterEvent,
  onTryAgainEvent,
}) => {
  const [remainingMs, setRemainingMs] = useState(timeoutMs);

  useEffect(() => {
    if (type !== 'Loading') {
      setRemainingMs(timeoutMs);
      return;
    }

    setRemainingMs(timeoutMs);

    const timer = window.setInterval(() => {
      setRemainingMs((previous) => Math.max(0, previous - TIMER_TICK_MS));
    }, TIMER_TICK_MS);

    return () => window.clearInterval(timer);
  }, [type, timeoutMs]);

  const timedOut = type === 'Timeout' || (type === 'Loading' && remainingMs === 0);

  const progressValue = useMemo(() => {
    if (timeoutMs <= 0) return 0;
    return Math.max(0, Math.min(100, (remainingMs / timeoutMs) * 100));
  }, [remainingMs, timeoutMs]);

  if (type === 'Loading' && !timedOut) {
    const cfg = CONFIG.Loading;
    return (
      <Box sx={{ display: 'block', width: 'fit-content', margin: '100px auto 10px auto' }}>
        <Stack spacing={1.5} alignItems="center">
          <CircularProgress
            id="qa-spinner-ico"
            variant="determinate"
            value={progressValue}
            size={50}
          />
          <Typography variant="body2" color="text.secondary">{message ?? cfg.defaultMessage}</Typography>
        </Stack>
      </Box>
    );
  }

  const effectiveType: TableNotificationType = timedOut ? 'Timeout' : type;

  if (effectiveType === 'Filter' || effectiveType === 'Empty') {
    return (
      <Stack
        id="qa-tableNotification"
        alignItems="center"
        textAlign="center"
        spacing={1}
        sx={{ py: 8, px: 2 }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            backgroundColor: '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <DescriptionIcon sx={{ fontSize: 48, color: '#757575' }} />
        </Box>
        <Typography id="qa-notificationHeader-lbl" variant="h6" sx={{ fontWeight: 500 }}>
          {NO_RECORDS_TITLE}
        </Typography>
        <Typography id="qa-notificationmessage-lbl" variant="body1">
          {message ?? NO_RECORDS_MESSAGE}
        </Typography>
        {!message && (
          <Typography variant="body1">{NO_RECORDS_HINT}</Typography>
        )}
        {onEditFilterEvent && (
          <Button
            id="qa-editFilters-btn"
            variant="contained"
            onClick={onEditFilterEvent}
            sx={{ mt: 2, backgroundColor: '#0c479d', letterSpacing: '0.08em', fontWeight: 700 }}
          >
            EDIT FILTERS
          </Button>
        )}
      </Stack>
    );
  }

  const cfg = CONFIG[effectiveType];

  const action = (effectiveType === 'Error' || effectiveType === 'Timeout')
    ? onTryAgainEvent
    : undefined;

  const actionLabel = action ? 'TRY AGAIN' : undefined;

  return (
    <Box sx={{ my: 2 }}>
      <Alert severity={cfg.severity}>
        <AlertTitle>{cfg.title}</AlertTitle>
        {message ?? cfg.defaultMessage}
        {action && actionLabel && (
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" onClick={action}>{actionLabel}</Button>
          </Box>
        )}
      </Alert>
    </Box>
  );
};
